import { ApolloError, useQuery } from "@apollo/client";

import {
  SubaccountOptOutNumberDetailsQuery,
  SubaccountOptOutNumberDetailsQueryVariables,
  TenantOptOutNumberDetailsQuery,
  TenantOptOutNumberDetailsQueryVariables,
} from "~frontend/__generated__/graphql";
import {
  GET_SUBACCOUNT_OPT_OUT_NUMBER_DETAILS,
  GET_TENANT_OPT_OUT_NUMBER_DETAILS,
} from "~frontend/graphql/optOuts";
import { useIntl } from "~frontend/providers/IntlProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";

export function useOptedOutNumberDetails(phoneNumber?: string | null): {
  data?:
    | SubaccountOptOutNumberDetailsQuery["subaccountOptOutNumberDetails"]
    | TenantOptOutNumberDetailsQuery["tenantOptOutNumberDetails"]
    | null;
  loading: boolean;
  error?: ApolloError | null;
  refetch: () => void;
} & (
  | {
      canDelete: true;
      cannotDeleteReason?: null;
    }
  | {
      canDelete: false;
      cannotDeleteReason?: string | null;
    }
) {
  const intl = useIntl();
  const subaccountContext = useSubaccount();

  const {
    data: subaccountNumberData,
    loading: subaccountNumberDataLoading,
    error: subaccountNumberError,
    refetch: refetchSubaccountNumberData,
  } = useQuery<
    SubaccountOptOutNumberDetailsQuery,
    SubaccountOptOutNumberDetailsQueryVariables
  >(GET_SUBACCOUNT_OPT_OUT_NUMBER_DETAILS, {
    variables: {
      input: {
        subaccountId: subaccountContext?.subaccount.id ?? "",
        phoneNumber: phoneNumber ?? "",
      },
    },
    skip: !phoneNumber || !subaccountContext,
    fetchPolicy: "network-only",
  });

  const {
    data: tenantNumberData,
    loading: tenantNumberDataLoading,
    error: tenantNumberError,
    refetch: refetchTenantNumberData,
  } = useQuery<
    TenantOptOutNumberDetailsQuery,
    TenantOptOutNumberDetailsQueryVariables
  >(GET_TENANT_OPT_OUT_NUMBER_DETAILS, {
    variables: {
      input: {
        phoneNumber: phoneNumber ?? "",
      },
    },
    skip: !phoneNumber || !!subaccountContext,
    fetchPolicy: "network-only",
  });

  const data = subaccountContext
    ? subaccountNumberData?.subaccountOptOutNumberDetails
    : tenantNumberData?.tenantOptOutNumberDetails;
  const loading = subaccountContext
    ? subaccountNumberDataLoading
    : tenantNumberDataLoading;
  const error = subaccountContext ? subaccountNumberError : tenantNumberError;
  const refetch = subaccountContext
    ? refetchSubaccountNumberData
    : refetchTenantNumberData;

  const numberHasActiveExternalOptOuts =
    data?.optOuts.some(
      optOut =>
        optOut.__typename === "AnonymousOptOut" && optOut.deletedAt === null
    ) ||
    data?.temporaryOptOuts.some(
      temporaryOptOut =>
        temporaryOptOut.__typename === "AnonymousTemporaryOptOut" &&
        temporaryOptOut.deletedAt === null &&
        temporaryOptOut.expiresAt > new Date().valueOf()
    ) ||
    false;

  if (!data) {
    return {
      data,
      loading,
      error,
      refetch,
      canDelete: false,
    };
  } else if (numberHasActiveExternalOptOuts) {
    return {
      data,
      loading,
      error,
      refetch,
      canDelete: false,
      cannotDeleteReason: intl.formatMessage({
        defaultMessage: "Cannot reverse external Opt Outs",
        description:
          "Message explaining that external Opt Outs cannot be deleted",
      }),
    };
  } else {
    return {
      data,
      loading,
      error,
      refetch,
      canDelete: true,
    };
  }
}
